import { ArrowLeft } from 'lucide-react';
import type { CSSProperties } from 'react';
import { Outlet, useMatches, useNavigate } from '@tanstack/react-router';

import { AppSidebar } from './AppSidebar';
import { Breadcrumb } from './Breadcrumb';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePageHeader } from './PageHeaderContext';
import { PersonalUserMenu } from './PersonalUserMenu';
import { PersonalInboxButton } from './PersonalInboxButton';
import { useIsPersonalEdition } from '@/lib/auth/useEdition';
import { RecordingGuard } from '@/components/audio/RecordingGuard';
import { CompactAppearanceToggle } from '@/components/appearance-toggle';
import { FloatingRecorderPill } from '@/components/audio/FloatingRecorderPill';
import { EncounterSessionProvider } from '@/components/audio/EncounterSessionProvider';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { buildPersonalTrail, type Crumb, HOME_CRUMB, parentHref } from './personalTrail';

interface AppShellHeaderProps {
  hideSidebarTrigger?: boolean;
}

interface DesktopTitleAreaProps {
  hideSidebarTrigger: boolean;
  personalTrail?: Crumb[];
}

interface MobileLeadingProps {
  hideSidebarTrigger: boolean;
  isInnerPage: boolean;
  onBack: () => void;
}

/**
 * Application shell.
 *
 * The shell intentionally keeps a single, stable JSX tree across the
 * professional/personal edition split. Both editions render
 * `<SidebarProvider><SidebarInset>…<Outlet /></SidebarInset></SidebarProvider>`;
 * the sidebar is shown or hidden inside that stable tree, and edition-specific
 * header chrome is selected by props. This avoids the React reconciliation
 * pitfall where swapping the root element type (`<SidebarProvider>` ↔ `<div>`)
 * unmounts the entire route subtree, leaving TanStack Router's matched
 * `<Outlet>` content stuck in a torn state.
 *
 * `PageHeaderProvider` lives above `<RouterProvider>` in `main.tsx`. Putting
 * it there means every route — at any depth — reads the same provider via
 * plain React context, regardless of how the router stitches matches.
 */
export function AppShell() {
  const { isLoading } = useAuth();

  // Defer rendering until AuthContext settles. Otherwise the first paint
  // reads `organization === null`, picks the professional shell, then flips
  // to personal once the org arrives — the resulting branch swap fights
  // TanStack Router's match reconciliation.
  if (isLoading) return null;

  return (
    <EncounterSessionProvider>
      <ShellLayout />
      <FloatingRecorderPill />
      <RecordingGuard />
    </EncounterSessionProvider>
  );
}

/**
 * The design's sidebar geometry: 244px expanded, collapsing to a 76px icon rail.
 * The shadcn primitive defaults to 16rem / 3rem, and overriding it here rather
 * than in sidebar.tsx keeps the primitive stock and upgradable.
 */
const SIDEBAR_WIDTH_VARS = {
  '--sidebar-width': '244px',
  '--sidebar-width-icon': '76px',
} as CSSProperties;

function AppShellHeader({ hideSidebarTrigger = false }: AppShellHeaderProps) {
  const { header } = usePageHeader();
  const navigate = useNavigate();
  const matches = useMatches();

  // The deepest route match that defines breadcrumbs in its context wins.
  // Cloning the array first so we don't mutate the value the router gave us.
  const matchWithTrail = [...matches]
    .reverse()
    .find((m) => (m.context as { breadcrumbs?: Crumb[] } | undefined)?.breadcrumbs);
  const routeTrail = (matchWithTrail?.context as { breadcrumbs?: Crumb[] } | undefined)?.breadcrumbs;

  // Personal edition renders a Home-rooted trail with live titles and
  // page-provided overrides. Professional keeps the raw route trail and its
  // existing back-to-root behavior.
  const personalTrail = hideSidebarTrigger
    ? buildPersonalTrail(routeTrail, header?.title, header?.breadcrumbs)
    : undefined;

  const isInnerPage = personalTrail ? personalTrail.length > 1 : !!routeTrail && routeTrail.length > 1;
  const backTarget = personalTrail ? (parentHref(personalTrail) ?? HOME_CRUMB.href) : '/';
  const handleBack = () => navigate({ to: backTarget });

  return (
    <header className="sticky top-0 z-50 overflow-hidden rounded-t-xl border-b border-[rgb(var(--fgc)/0.08)] bg-[rgb(var(--bgc)/0.86)] backdrop-blur-[14px]">
      <div className="flex h-14 items-center gap-4 px-4">
        <MobileLeading isInnerPage={isInnerPage} hideSidebarTrigger={hideSidebarTrigger} onBack={handleBack} />

        {!hideSidebarTrigger && (
          <div className="hidden md:flex">
            <SidebarTrigger />
          </div>
        )}

        <DesktopTitleArea personalTrail={personalTrail} hideSidebarTrigger={hideSidebarTrigger} />

        {header?.title && (
          <div className="flex min-w-0 flex-1 items-center justify-between md:hidden">
            <h1 className="truncate text-base font-semibold">{header.title}</h1>
          </div>
        )}

        {header?.actions && <div className="flex shrink-0 items-center gap-2">{header.actions}</div>}

        {/* ml-auto so the theme control and the personal menu sit right when no
            preceding element claims the free space. */}
        <CompactAppearanceToggle className="ml-auto" />

        {hideSidebarTrigger && (
          <div className="flex shrink-0 items-center gap-1">
            <PersonalInboxButton />
            <PersonalUserMenu />
          </div>
        )}
      </div>
    </header>
  );
}

function DesktopTitleArea({ personalTrail, hideSidebarTrigger }: DesktopTitleAreaProps) {
  if (hideSidebarTrigger) {
    return (
      <div className="hidden min-w-0 flex-1 items-center md:flex">
        <Breadcrumb trail={personalTrail} />
      </div>
    );
  }
  return (
    <div className="hidden flex-1 items-center md:flex">
      <Breadcrumb />
    </div>
  );
}

function MobileLeading({ onBack, isInnerPage, hideSidebarTrigger }: MobileLeadingProps) {
  return (
    <div className="flex items-center gap-2 md:hidden">
      {isInnerPage ? (
        <Button size="icon" variant="ghost" className="h-9 w-9" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          <span className="sr-only">Go back</span>
        </Button>
      ) : hideSidebarTrigger ? null : (
        <SidebarTrigger />
      )}
    </div>
  );
}

function ShellLayout() {
  const isPersonal = useIsPersonalEdition();

  return (
    <SidebarProvider style={SIDEBAR_WIDTH_VARS}>
      {!isPersonal && <AppSidebar />}
      <SidebarInset>
        <AppShellHeader hideSidebarTrigger={isPersonal} />
        <div className="flex flex-1 flex-col mb-10">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
